import { getApiBase } from '../shared/utils/urlHelpers';
import { setGuidedOnboardingPasswordChangeRequired } from '../app/guidedOnboarding/guidedOnboardingConstants.js';
const TOKEN_KEY = 'nitromule_token';
const CART_SESSION_KEY = 'nm_cart_id';

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function isStoredAuthTokenUsable(token) {
  const raw = String(token || '').trim();
  if (!raw) return false;
  const payload = decodeJwtPayload(raw);
  if (!payload || !payload.userId || payload.mfa_pending) return false;
  if (typeof payload.exp === 'number') {
    const nowSec = Math.floor(Date.now() / 1000);
    if (payload.exp <= nowSec) return false;
  }
  return true;
}

function clearStoredAuthToken() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  if (window.nitromuleReactData && typeof window.nitromuleReactData === 'object') {
    window.nitromuleReactData.token = '';
    window.nitromuleReactData.userLoggedIn = false;
  }
}
export const getCartSessionId = () => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(CART_SESSION_KEY) || sessionStorage.getItem(CART_SESSION_KEY) || '';
};
export const setCartSessionId = id => {
  if (typeof window === 'undefined') return;
  if (id) {
    try {
      localStorage.setItem(CART_SESSION_KEY, id);
    } catch {
      sessionStorage.setItem(CART_SESSION_KEY, id);
    }
    sessionStorage.setItem(CART_SESSION_KEY, id);
  } else {
    localStorage.removeItem(CART_SESSION_KEY);
    sessionStorage.removeItem(CART_SESSION_KEY);
  }
};
export const getToken = () => {
  if (typeof window === 'undefined') return '';
  const token = localStorage.getItem(TOKEN_KEY) || window.nitromuleReactData?.token || '';
  if (!token) return '';
  if (!isStoredAuthTokenUsable(token)) {
    clearStoredAuthToken();
    return '';
  }
  return token;
};
export const setToken = token => {
  if (typeof window === 'undefined') return;
  if (token && isStoredAuthTokenUsable(token)) {
    clearLoggedOutLocally();
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    clearStoredAuthToken();
  }
};

const LOGOUT_BYPASS_KEY = 'nm_logged_out';

export function markLoggedOutLocally() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LOGOUT_BYPASS_KEY, '1');
    sessionStorage.removeItem(LOGOUT_BYPASS_KEY);
  } catch (_) {}
  clearStoredAuthToken();
}

export function isLoggedOutLocally() {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(LOGOUT_BYPASS_KEY) === '1' || sessionStorage.getItem(LOGOUT_BYPASS_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearLoggedOutLocally() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(LOGOUT_BYPASS_KEY);
    sessionStorage.removeItem(LOGOUT_BYPASS_KEY);
  } catch (_) {}
}

/** @deprecated Prefer isLoggedOutLocally(); kept for callers that expect consume semantics. */
export function consumeLoggedOutBypass() {
  return isLoggedOutLocally();
}

export function getPostLogoutRedirectUrl() {
  if (typeof window === 'undefined') return '/';
  if (window.nitromuleDesktop?.isDesktopApp) {
    return '/auth/desktop/wait';
  }
  const portal = !!window.nitromuleReactData?.portalConfig;
  if (portal) {
    const redirectPage = window.nitromuleReactData?.quoteRedirectPage || 'get-label';
    return `/${String(redirectPage).replace(/^\//, '')}`;
  }
  return '/login';
}

export async function logoutCurrentSession() {
  if (typeof window === 'undefined') return;
  if (window.nitromuleDesktop?.clearSession) {
    try {
      await window.nitromuleDesktop.clearSession();
    } catch (_) {}
  }
  markLoggedOutLocally();
  try {
    await apiRequest('nitromule/v1/auth/logout', {
      method: 'POST',
      noAuth: true
    });
  } catch (e) {
    console.warn('Logout request failed:', e?.message || e);
  }
  try {
    const {
      invalidateShippingPreferencesCache
    } = await import('../account/settings/useUserShippingPreferences.js');
    invalidateShippingPreferencesCache();
  } catch (_) {}
}
const normalizeEndpoint = endpoint => {
  if (endpoint.startsWith('/')) endpoint = endpoint.substring(1);
  return endpoint;
};

function isPlainJsonBody(body) {
  if (body == null || typeof body !== 'object') return false;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return false;
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return false;
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(body)) return false;
  if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) return false;
  return true;
}

export const apiRequest = async (endpoint, options = {}) => {
  const base = getApiBase();
  const path = normalizeEndpoint(endpoint);
  const url = path ? `${base}/${path}` : base;
  const token = options.noAuth ? undefined : getToken();
  const rawBody = options.body;
  const isFormData = typeof FormData !== 'undefined' && rawBody instanceof FormData;
  const body = isPlainJsonBody(rawBody) ? JSON.stringify(rawBody) : rawBody;
  const cartSession = getCartSessionId();
  const embedPublicId = typeof window !== 'undefined' && window.__NM_QUOTE_EMBED_PUBLIC_ID__ ? String(window.__NM_QUOTE_EMBED_PUBLIC_ID__).trim() : '';
  const headers = {
    ...(!isFormData && {
      'Content-Type': 'application/json'
    }),
    ...(token && {
      Authorization: `Bearer ${token}`
    }),
    ...(cartSession && {
      'X-Cart-Session': cartSession
    }),
    ...(embedPublicId && {
      'X-NM-Quote-Embed-Public-Id': embedPublicId
    }),
    ...(options.headers || {})
  };
  const {
    noAuth: _noAuth,
    body: _rawBody,
    ...fetchOptions
  } = options;
  const config = {
    credentials: 'include',
    ...fetchOptions,
    headers,
    ...(body !== undefined ? { body } : {})
  };
  return fetch(url, config);
};
function messageFromErrorBody(errorData, fallbackStatus) {
  if (!errorData || typeof errorData !== 'object') {
    return `HTTP ${fallbackStatus}`;
  }
  const fromNested = errorData.response?.errors?.[0]?.message;
  if (typeof fromNested === 'string' && fromNested.trim()) return fromNested.trim();
  const d = errorData.detail;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (Array.isArray(d) && d[0]) {
    const first = d[0];
    if (typeof first === 'string') return first;
    if (first?.msg) return String(first.msg);
  }
  if (typeof errorData.message === 'string' && errorData.message.trim()) return errorData.message.trim();
  if (typeof errorData.error === 'string' && errorData.error.trim()) return errorData.error.trim();
  return `HTTP ${fallbackStatus}`;
}
export function getAccountDetailsRedirectPath() {
  if (typeof window === 'undefined') return '/account/profile';
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const portal = !!window.nitromuleReactData?.portalConfig;
  if (portal) {
    return `${path.replace(/\/[^/]+$/, '')}/profile`.replace(/\/{2,}/g, '/');
  }
  return '/account/profile';
}
async function readResponseJsonBody(response) {
  const text = await response.text();
  if (!text || !String(text).trim()) return {};
  try {
    return JSON.parse(text);
  } catch (err) {
    const error = new Error(err?.message || 'Invalid JSON response');
    error.status = response.status;
    error.response = { raw: text.slice(0, 200) };
    throw error;
  }
}

export const apiRequestJson = async (endpoint, options = {}) => {
  const response = await apiRequest(endpoint, options);
  if (!response.ok) {
    let errorData;
    try {
      errorData = await readResponseJsonBody(response);
    } catch (e) {
      errorData = {};
    }
    if (response.status === 403 && errorData?.code === 'password_change_required' && typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/$/, '') || '/';
      const onAccountPage = path.endsWith('/profile') || path.endsWith('/account/profile');
      setGuidedOnboardingPasswordChangeRequired(true);
      if (!onAccountPage) {
        window.location.assign(getAccountDetailsRedirectPath());
      }
    }
    const msg = messageFromErrorBody(errorData, response.status);
    const error = new Error(msg);
    error.response = errorData;
    error.status = response.status;
    if (errorData && typeof errorData.code === 'string') {
      error.code = errorData.code;
    }
    const requestIdFromBody =
      (errorData && typeof errorData.request_id === 'string' && errorData.request_id.trim()) ||
      (errorData?.error && typeof errorData.error.request_id === 'string' && errorData.error.request_id.trim()) ||
      '';
    const requestIdFromHeader = (response.headers?.get?.('X-Request-Id') || '').trim();
    const requestId = requestIdFromBody || requestIdFromHeader;
    if (requestId) {
      error.requestId = requestId;
    }
    throw error;
  }
  // 204 / empty 200 bodies are valid for some endpoints; don't throw Firefox's
  // "JSON.parse: unexpected end of data" into the UI.
  if (response.status === 204) return {};
  return readResponseJsonBody(response);
};
